import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HelpCircle, Plus, Minus } from 'lucide-react';
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
type Faq = {
  question: string;
  answer: React.ReactNode;
};
const faqs: Faq[] = [{
  question: '¿Esto es terapia?',
  answer: <>
        No es terapia clínica. Es una conversación guiada con herramientas
        prácticas de transformación personal, fe y finanzas.{' '}
        <span className="text-navy-900 font-medium" data-id="element-24">
          Muchas veces hace lo que la terapia no toca.
        </span>
      </>
}, {
  question: '¿Qué pasa si no soy muy religioso?',
  answer: <>
        No importa tu nivel de fe. Hablamos de Dios desde un lugar de{' '}
        <span className="text-navy-900 font-medium" data-id="element-25">
          verdad, no de religión
        </span>
        . Nadie te va a juzgar ni presionar.
      </>
}, {
  question: '¿Y si mi caso es muy complicado?',
  answer: <>
        Los casos "complicados" son los que más responden. Porque la raíz está
        clara — solo hace falta alguien que ayude a verla.
      </>
}, {
  question: '¿Qué voy a obtener en UNA sola charla?',
  answer: <>
        Claridad. Dirección. Al menos{' '}
        <span className="text-navy-900 font-medium" data-id="element-26">
          una herramienta concreta
        </span>{' '}
        que puedes aplicar ese mismo día. Y muchas veces, eso cambia todo.
      </>
}, {
  question: '¿No es esto solo hablar sin resultado?',
  answer: <>
        Cada charla termina con un{' '}
        <span className="text-navy-900 font-medium" data-id="element-27">
          plan de acción personalizado
        </span>
        . No te vas con solo palabras bonitas — te vas con pasos concretos.
      </>
}];
export function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const toggle = (i: number) => {
    setOpenIndex(openIndex === i ? null : i);
  };
  return <section className="py-14 px-4 bg-navy-50 relative overflow-hidden" data-id="element-28">
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gold-50 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3 opacity-60 pointer-events-none" data-id="element-29" />

      <div className="max-w-3xl mx-auto relative z-10" data-id="element-30">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center mb-14" data-id="element-31">
          <div className="inline-flex items-center gap-2 text-navy-600 mb-6" data-id="element-32">
            <HelpCircle className="w-6 h-6" data-id="element-33" />
            <h2 className="text-sm font-bold tracking-widest uppercase" data-id="element-34">
              Preguntas Frecuentes
            </h2>
          </div>
          <h3 className="text-4xl md:text-5xl font-serif font-bold text-navy-900 mb-4" data-id="element-35">
            Lo que probablemente estás pensando
          </h3>
          <p className="text-lg text-navy-600" data-id="element-36">
            Respuestas honestas a las dudas más comunes antes de dar el paso.
          </p>
        </motion.div>

        <motion.ul initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="space-y-4" data-id="element-37">
          {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return <li key={i} className={`bg-white rounded-2xl border transition-colors duration-300 shadow-sm ${isOpen ? 'border-gold-400 shadow-md' : 'border-navy-100 hover:border-navy-200'}`} data-id="element-38">
                <button onClick={() => toggle(i)} aria-expanded={isOpen} aria-controls={`faq-panel-${i}`} className="w-full flex items-center justify-between gap-6 text-left px-6 md:px-8 py-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400 rounded-2xl" data-id="element-39">
                  <span className="font-serif text-lg md:text-xl text-navy-900 font-semibold leading-snug" data-id="element-40">
                    {faq.question}
                  </span>
                  <span className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-colors duration-300 ${isOpen ? 'bg-gold-500 text-white' : 'bg-navy-50 text-navy-700'}`} data-id="element-41">
                    {isOpen ? <Minus className="w-4 h-4" data-id="element-42" /> : <Plus className="w-4 h-4" data-id="element-43" />}
                  </span>
                </button>

                <AnimatePresence initial={false} data-id="element-44">
                  {isOpen && <motion.div id={`faq-panel-${i}`} key="content" initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.3,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-45">
                      <div className="px-6 md:px-8 pb-6 pt-0" data-id="element-46">
                        <div className="border-l-4 border-l-gold-400 pl-4 text-navy-700 leading-relaxed text-base md:text-lg" data-id="element-47">
                          {faq.answer}
                        </div>
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </li>;
        })}
        </motion.ul>

        <motion.p initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-100px'
      }} variants={fadeIn} className="text-center text-navy-600 mt-12 italic font-serif text-lg" data-id="element-48">
          ¿Tienes otra duda? Pregúntala antes de la charla — sin compromiso.
        </motion.p>
      </div>
    </section>;
}